// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse } from 'axios';
import CustomPagination, {
  CustomPaginationProps,
} from 'components/atoms/CustomPagination';
import { useState } from 'react';
import { DEFAULT_PAGE, PAGE_SIZE } from 'util/ConstantValues';
import { handleError } from 'util/errorHandler';
import _pick from 'lodash/pick';
import { useHistory, useLocation } from 'react-router';
import queryString from 'query-string';
import { Form } from 'react-final-form';
import { SelectField } from 'components/molecules/SelectField';
import { perPageOptions } from 'util/siteManagementTypes';
import { useTranslation } from 'react-i18next';

export function usePagination<
  Params extends {
    page?: number;
    take?: number;
  },
  Response extends {
    page: number;
    total: number;
  }
>(params: {
  callback: (params: Params) => Promise<Response | AxiosResponse<Response>>;
  queryParamKeys?: Exclude<keyof Params, 'page' | 'take'>[];
  successCallback?: (res: Response, isReset?: boolean) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
  defaultPage?: number;
  defaultPageSize?: number;
  perPageControl?: boolean;
}) {
  const {
    callback,
    successCallback,
    failedCallback,
    finallyCallback,
    defaultPage = DEFAULT_PAGE,
    defaultPageSize = PAGE_SIZE,
    queryParamKeys = [],
    perPageControl = true,
  } = params;
  const history = useHistory();
  const location = useLocation();
  const paramKeys: (keyof Params)[] = ['page', 'take', ...queryParamKeys];
  const { t } = useTranslation();
  const urlParams = queryString.parse(location.search);

  const [pageMeta, setPageMeta] = useState<
    Params & {
      total: number;
    }
  >({
    page: defaultPage,
    take: defaultPageSize,
    total: 0,
    ...queryString.parse(location.search),
  } as any);
  const [data, setData] = useState<Response>();

  const updateQueryParams = (params?: Params) => {
    history.replace({
      pathname: history.location.pathname,
      search: queryString.stringify(
        Object.fromEntries(
          Object.entries({
            ...queryString.parse(location.search),
            ...params,
          }).filter(
            ([key]) =>
              !paramKeys.includes(key as keyof Params) ||
              (params?.[key] != null && params[key] !== '')
          )
        )
      ),
      hash: location.hash,
    });
  };

  const triggerFetch = async (
    p?: Partial<Params> & {
      reset?: boolean;
    }
  ) => {
    const { reset, ...params } = p || {};

    const newQueryData = _pick(params, queryParamKeys);
    const oldQueryData = _pick(pageMeta, queryParamKeys);

    const isQueryChanged = Object.keys(newQueryData).some(
      (key) => newQueryData[key] !== oldQueryData[key]
    );

    const getParams = !reset
      ? {
          ..._pick(pageMeta, paramKeys),
          ...params,
          ...(isQueryChanged && {
            page: defaultPage,
          }),
        }
      : ({
          ...params,
          page: defaultPage,
          take: pageMeta.take,
        } as Params);

    try {
      const res = await callback(getParams);

      if ('headers' in res && 'data' in res && 'status' in res) {
        setData(res.data);
        setPageMeta({
          ...getParams,
          page: res.data.page,
          total: res.data.total,
        } as any);

        successCallback?.(res.data, reset);
      } else {
        setData(res);
        setPageMeta({
          ...getParams,
          page: res.page,
          total: res.total,
        } as any);

        successCallback?.(res, reset);
      }

      updateQueryParams(getParams);
    } catch (error) {
      handleError(error);
      failedCallback?.(error);
    } finally {
      finallyCallback?.();
    }
  };

  const getPageCount = (perPage?: number) => {
    return Math.ceil(pageMeta.total / (perPage || defaultPageSize));
  };

  const handleOnChangePerPage = (perPage: number | string) => {
    const currentPage = urlParams.page;
    const newPageCount = getPageCount(Number(perPage));
    const newSelectedPage = Math.min(currentPage, newPageCount);

    triggerFetch({ page: newSelectedPage, take: perPage } as Params);
  };

  const renderPagination = (props?: Partial<CustomPaginationProps>) => {
    if (pageMeta.total <= defaultPageSize) {
      return null;
    }

    return (
      <div className="paginationContainer">
        <CustomPagination
          pageCount={getPageCount(pageMeta.take)}
          forcePage={(pageMeta.page ?? defaultPage) - 1}
          onPageChange={({ selected }) =>
            triggerFetch({
              page: selected + 1,
            } as Params)
          }
          {...props}
        />

        {perPageControl && (
          <Form<{ id: number }>
            onSubmit={(value) => {
              value.id;
            }}
          >
            {() => (
              <form className="perPageForm">
                <div
                  style={{
                    color: props?.mode === 'light' ? '#000' : '#b7b7b7',
                  }}
                >
                  {t('pagination.per_page')}
                </div>
                <SelectField
                  isDarkmode={props?.mode !== 'light'}
                  name="perPage"
                  required
                  options={perPageOptions}
                  value={urlParams.take}
                  menuPlacement="top"
                  onChange={(value) => handleOnChangePerPage(value)}
                />
              </form>
            )}
          </Form>
        )}
      </div>
    );
  };

  return {
    data,
    triggerFetch,
    renderPagination,
    pageMeta,
  };
}
